/**
 * Registration Details Constants
 * Centralized constants for registration status options and delivery times
 */

/**
 * Auto-hide duration for success messages (in milliseconds)
 */
export const SUCCESS_MESSAGE_DURATION_MS = 3000;

/**
 * PDI (Pre-Delivery Inspection) status values
 * Used to determine vehicle readiness state
 */
export const PDI_STATUS = {
  NOT_STARTED: 'not started',
  IN_PROGRESS: 'in progress',
  COMPLETED: 'complete',
} as const;

/**
 * PDI statuses that indicate the vehicle is ready or being prepared for scheduling
 * Used to determine when to display Vehicle Ready to Schedule Date
 * Shows dates when status is "In progress" or "Completed"
 */
export const PDI_STATUSES_WITH_READY_DATE = [PDI_STATUS.COMPLETED, PDI_STATUS.IN_PROGRESS];

/**
 * Helper function to check if PDI Completed Date should be displayed
 * @param status - The PDI status to check (case-insensitive)
 * @returns true only if status is 'completed'
 * @description
 * - "Not started" → false (show "-")
 * - "In progress" → true (show date)
 * - "Completed" → true (show date if available, otherwise "-")
 */
export const isPdiStatusReady = (status?: string | null): boolean => {
  if (!status) return false;
  const normalized = status.toLowerCase();
  return normalized.startsWith(PDI_STATUS.COMPLETED) || normalized === PDI_STATUS.IN_PROGRESS;
};

/**
 * Registration status options for the dropdown field
 * @param labels - i18n labels from Contentful
 * @returns Array of registration status options with label and value properties for dropdown selection
 */
export const getRegistrationStatusOptions = (labels?: Record<string, string>) => [
  { label: labels?.['const.registrationStatus.none'] ?? '--None--', value: '' },
  { label: labels?.['const.registrationStatus.created'] ?? 'Created', value: 'Created' },
  {
    label: labels?.['const.registrationStatus.sentToCustomer'] ?? 'Sent to Customer',
    value: 'Sent to Customer',
  },
  { label: labels?.['const.registrationStatus.hold'] ?? 'Hold', value: 'Hold' },
  {
    label: labels?.['const.registrationStatus.pendingDocuments'] ?? 'Pending Documents',
    value: 'Pending Documents',
  },
  {
    label: labels?.['const.registrationStatus.documentsReceived'] ?? 'Documents Received',
    value: 'Documents Received',
  },
  {
    label:
      labels?.['const.registrationStatus.sentToRegistrationOffice'] ??
      'Sent to Registration Office',
    value: 'Sent to Registration Office',
  },
  {
    label: labels?.['const.registrationStatus.deregistered'] ?? 'Deregistered',
    value: 'Deregistered',
  },
  { label: labels?.['const.registrationStatus.registered'] ?? 'Registered', value: 'Registered' },
  { label: labels?.['const.registrationStatus.finalized'] ?? 'Finalized', value: 'Finalized' },
];

/**
 * Scheduled delivery time options (H:mm values matching backend format, 12h display labels)
 * @param labels - i18n labels from Contentful
 * @returns Array of delivery time options with label and value properties for dropdown selection
 */
export const getDeliveryTimeOptions = (labels?: Record<string, string>) => [
  { label: labels?.['const.deliveryTime.9am'] ?? '9:00 AM', value: '9:00' },
  { label: labels?.['const.deliveryTime.10am'] ?? '10:00 AM', value: '10:00' },
  { label: labels?.['const.deliveryTime.11am'] ?? '11:00 AM', value: '11:00' },
  { label: labels?.['const.deliveryTime.12pm'] ?? '12:00 PM', value: '12:00' },
  { label: labels?.['const.deliveryTime.1pm'] ?? '1:00 PM', value: '13:00' },
  { label: labels?.['const.deliveryTime.2pm'] ?? '2:00 PM', value: '14:00' },
  { label: labels?.['const.deliveryTime.3pm'] ?? '3:00 PM', value: '15:00' },
  { label: labels?.['const.deliveryTime.4pm'] ?? '4:00 PM', value: '16:00' },
  { label: labels?.['const.deliveryTime.5pm'] ?? '5:00 PM', value: '17:00' },
  { label: labels?.['const.deliveryTime.6pm'] ?? '6:00 PM', value: '18:00' },
  { label: labels?.['const.deliveryTime.7pm'] ?? '7:00 PM', value: '19:00' },
  { label: labels?.['const.deliveryTime.8pm'] ?? '8:00 PM', value: '20:00' },
  { label: labels?.['const.deliveryTime.9pm'] ?? '9:00 PM', value: '21:00' },
];
